import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { toast } from 'react-toastify';
import { usersApi } from '../../api/users';
import { providersApi } from '../../api/providers';
import { jobsApi } from '../../api/jobs';
import { paymentsApi } from '../../api/payments';
import { reviewsApi } from '../../api/reviews';
import { servicesApi } from '../../api/services';
import { reportsApi } from '../../api/reports';
import {
  PageHeader,
  PageFilters,
  DataTable,
  StatCard,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { DATE_RANGE_PRESETS } from '../../constants';
import { formatCurrency, formatDate } from '../../utils';
import type {
  Complaint,
  Customer,
  Payment,
  Review,
  ServiceCategory,
  ServiceProvider,
  ServiceRequest,
} from '../../types';

type ReportType =
  | 'Users'
  | 'Providers'
  | 'Jobs'
  | 'Payments'
  | 'Complaints'
  | 'Reviews'
  | 'Categories';

const COLORS = ['#10B981', '#1E3A8A', '#F59E0B', '#EF4444', '#2563EB', '#64748B'];

const ReportsPage: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('Users');
  const [preset, setPreset] = useState('last30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [jobs, setJobs] = useState<ServiceRequest[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);

  useEffect(() => {
    Promise.all([
      usersApi.getUsers({ page: 1, limit: 1000 }),
      providersApi.getProviders({ page: 1, limit: 1000 }),
      jobsApi.getJobs({ page: 1, limit: 1000 }),
      paymentsApi.getJobPayments({ page: 1, limit: 1000 }),
      reportsApi.getReports({ page: 1, limit: 1000 }),
      reviewsApi.getReviews({ page: 1, limit: 1000 }),
      servicesApi.getAllCategories(),
    ])
      .then(([c, p, j, pay, cmp, rev, cat]) => {
        setCustomers(c.users);
        setProviders(p.providers);
        setJobs(j.jobs);
        setPayments(pay.payments);
        setComplaints(cmp.reports);
        setReviews(rev.reviews);
        setCategories(cat);
      })
      .catch(() => toast.error('Failed to load report data'))
      .finally(() => setLoading(false));
  }, []);

  const report = useMemo(() => {
    const inRange = (iso: string) => {
      if (preset === 'custom') {
        if (startDate && new Date(iso) < new Date(startDate)) return false;
        if (endDate && new Date(iso) > new Date(`${endDate}T23:59:59`)) return false;
        return true;
      }
      const now = new Date();
      const d = new Date(iso);
      const startOfDay = (date: Date) =>
        new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const today = startOfDay(now);
      if (preset === 'today') return startOfDay(d).getTime() === today.getTime();
      if (preset === 'yesterday') {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        return startOfDay(d).getTime() === y.getTime();
      }
      if (preset === 'last7') {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        return d >= from;
      }
      if (preset === 'last30') {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        return d >= from;
      }
      if (preset === 'thisMonth') {
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }
      if (preset === 'lastMonth') {
        const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        return d.getMonth() === m && d.getFullYear() === y;
      }
      return true;
    };

    if (reportType === 'Users') {
      const rows = customers.filter((r) => inRange(r.registrationDate));
      const byStatus = ['Active', 'Suspended', 'Deactivated', 'Inactive'].map((s) => ({
        name: s,
        value: rows.filter((r) => r.status === s).length,
      }));
      return {
        metrics: [
          { label: 'Total Users', value: rows.length },
          { label: 'Active', value: rows.filter((r) => r.status === 'Active').length },
          { label: 'Suspended', value: rows.filter((r) => r.status === 'Suspended').length },
        ],
        chart: byStatus.filter((x) => x.value > 0),
        chartType: 'pie' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.name,
          c2: r.email,
          c3: formatDate(r.registrationDate),
          c4: String(r.status),
        })),
        headers: ['ID', 'Name', 'Email', 'Registered', 'Status'],
      };
    }
    if (reportType === 'Providers') {
      const rows = providers.filter((r) => inRange(r.applicationDate));
      const byStatus = ['Pending', 'Approved', 'Rejected'].map((s) => ({
        name: s,
        value: rows.filter((r) => r.verificationStatus === s).length,
      }));
      return {
        metrics: [
          { label: 'Total Providers', value: rows.length },
          { label: 'Approved', value: rows.filter((r) => r.verificationStatus === 'Approved').length },
          { label: 'Pending', value: rows.filter((r) => r.verificationStatus === 'Pending').length },
        ],
        chart: byStatus.filter((x) => x.value > 0),
        chartType: 'bar' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.name,
          c2: r.primaryCategory,
          c3: formatDate(r.applicationDate),
          c4: String(r.verificationStatus),
        })),
        headers: ['ID', 'Name', 'Category', 'Applied', 'Verification'],
      };
    }
    if (reportType === 'Jobs') {
      const rows = jobs.filter((r) => inRange(r.createdAt));
      const byStatus = Array.from(new Set(rows.map((r) => r.status))).map((s) => ({
        name: s,
        value: rows.filter((r) => r.status === s).length,
      }));
      return {
        metrics: [
          { label: 'Total Jobs', value: rows.length },
          { label: 'Completed', value: rows.filter((r) => r.status === 'Completed').length },
          { label: 'Cancelled', value: rows.filter((r) => r.status === 'Cancelled').length },
        ],
        chart: byStatus,
        chartType: 'bar' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.customerName,
          c2: r.category,
          c3: formatDate(r.createdAt),
          c4: String(r.status),
        })),
        headers: ['ID', 'Customer', 'Category', 'Created', 'Status'],
      };
    }
    if (reportType === 'Payments') {
      const rows = payments.filter((r) => inRange(r.date));
      const byStatus = Array.from(new Set(rows.map((r) => r.status))).map((s) => ({
        name: s,
        value: rows.filter((r) => r.status === s).length,
      }));
      const total = rows
        .filter((r) => r.status === 'Successful')
        .reduce((sum, r) => sum + r.amount, 0);
      return {
        metrics: [
          { label: 'Transactions', value: rows.length },
          { label: 'Successful Volume', value: formatCurrency(total) },
          { label: 'Failed', value: rows.filter((r) => r.status === 'Failed').length },
        ],
        chart: byStatus,
        chartType: 'pie' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.customerName,
          c2: formatCurrency(r.amount),
          c3: formatDate(r.date),
          c4: String(r.status),
        })),
        headers: ['ID', 'Customer', 'Amount', 'Date', 'Status'],
      };
    }
    if (reportType === 'Complaints') {
      const rows = complaints.filter((r) => inRange(r.createdAt));
      const byStatus = Array.from(new Set(rows.map((r) => r.status))).map((s) => ({
        name: s,
        value: rows.filter((r) => r.status === s).length,
      }));
      return {
        metrics: [
          { label: 'Total Complaints', value: rows.length },
          { label: 'Open', value: rows.filter((r) => r.status === 'Open').length },
          { label: 'Resolved', value: rows.filter((r) => r.status === 'Resolved').length },
        ],
        chart: byStatus,
        chartType: 'bar' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.type,
          c2: r.jobId,
          c3: formatDate(r.createdAt),
          c4: String(r.status),
        })),
        headers: ['ID', 'Type', 'Job', 'Created', 'Status'],
      };
    }
    if (reportType === 'Reviews') {
      const rows = reviews.filter((r) => inRange(r.date));
      const byRating = [5, 4, 3, 2, 1].map((n) => ({
        name: `${n}★`,
        value: rows.filter((r) => r.rating === n).length,
      }));
      const avg =
        rows.length === 0
          ? 0
          : rows.reduce((sum, r) => sum + r.rating, 0) / rows.length;
      return {
        metrics: [
          { label: 'Total Reviews', value: rows.length },
          { label: 'Avg Rating', value: avg.toFixed(1) },
          { label: 'Hidden', value: rows.filter((r) => r.status === 'Hidden').length },
        ],
        chart: byRating.filter((x) => x.value > 0),
        chartType: 'bar' as const,
        tableRows: rows.slice(0, 20).map((r) => ({
          id: r.id,
          c1: r.reviewer,
          c2: String(r.rating),
          c3: formatDate(r.date),
          c4: String(r.status),
        })),
        headers: ['ID', 'Reviewer', 'Rating', 'Date', 'Status'],
      };
    }
    const rows = categories;
    const chart = rows.map((r) => ({ name: r.name, value: r.requestCount }));
    return {
      metrics: [
        { label: 'Categories', value: rows.length },
        { label: 'Active', value: rows.filter((r) => r.status === 'Active').length },
        { label: 'Total Requests', value: rows.reduce((s, r) => s + r.requestCount, 0) },
      ],
      chart,
      chartType: 'bar' as const,
      tableRows: rows.slice(0, 20).map((r) => ({
        id: r.id,
        c1: r.name,
        c2: String(r.providerCount),
        c3: String(r.requestCount),
        c4: String(r.status),
      })),
      headers: ['ID', 'Name', 'Providers', 'Requests', 'Status'],
    };
  }, [
    reportType,
    customers,
    providers,
    jobs,
    payments,
    complaints,
    reviews,
    categories,
    preset,
    startDate,
    endDate,
  ]);

  type TableRow = { id: string; c1: string; c2: string; c3: string; c4: string };

  const columns: Column<TableRow>[] = [
    { id: 'id', label: report.headers[0], render: (row) => row.id },
    { id: 'c1', label: report.headers[1], render: (row) => row.c1 },
    { id: 'c2', label: report.headers[2], render: (row) => row.c2 },
    { id: 'c3', label: report.headers[3], render: (row) => row.c3 },
    { id: 'c4', label: report.headers[4], render: (row) => row.c4 },
  ];

  return (
    <Box>
      <PageHeader
        title="Reports & Analytics"
        subtitle="Generate reports with predefined or custom date ranges"
      />

      <PageFilters
        filters={[
          {
            id: 'type',
            label: 'Report Type',
            value: reportType,
            options: [
              { label: 'Users', value: 'Users' },
              { label: 'Handy Persons', value: 'Providers' },
              { label: 'Jobs', value: 'Jobs' },
              { label: 'Payments', value: 'Payments' },
              { label: 'Complaints', value: 'Complaints' },
              { label: 'Reviews', value: 'Reviews' },
              { label: 'Categories', value: 'Categories' },
            ],
            onChange: (v) => setReportType(v as ReportType),
          },
          {
            id: 'preset',
            label: 'Date Range',
            value: preset,
            options: DATE_RANGE_PRESETS.map((p) => ({ label: p.label, value: p.value })),
            onChange: setPreset,
          },
        ]}
      >
        {preset === 'custom' && (
          <>
            <TextField
              size="small"
              type="date"
              label="Start"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="End"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </>
        )}
      </PageFilters>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {report.metrics.map((m) => (
          <Grid item xs={12} sm={4} key={m.label}>
            <StatCard
              label={m.label}
              value={m.value}
              icon={<AssessmentOutlinedIcon />}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: 300 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Distribution
              </Typography>
              {!loading && report.chart.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  {report.chartType === 'pie' ? (
                    <PieChart>
                      <Pie
                        data={report.chart}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={80}
                        label
                      >
                        {report.chart.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  ) : (
                    <BarChart data={report.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary">No chart data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 300 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                {reportType} Data
              </Typography>
              <DataTable
                columns={columns}
                rows={report.tableRows}
                loading={loading}
                getRowId={(row) => row.id}
                emptyTitle="No rows for this range"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPage;
